import { useState } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { speakWord } from '@/lib/index';
import {
  PINYIN_INITIALS,
  PINYIN_FINALS,
  HIRAGANA_ROWS,
  KATAKANA_ROWS,
  ENGLISH_LETTERS,
} from '@/data/phonicsData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type TabValue = 'pinyin' | 'kana' | 'alphabet';

export default function Phonics() {
  const [activeTab, setActiveTab] = useState<TabValue>('pinyin');

  const handlePinyinClick = (speakChar: string) => {
    speakWord(speakChar, 'zh');
  };

  const handleKanaClick = (char: string) => {
    speakWord(char, 'jp');
  };

  const handleAlphabetClick = (upper: string) => {
    speakWord(upper, 'en');
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background py-8 md:py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8 md:mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3 text-foreground">
              🎵 发音练习
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              三语发音基础 · 点击听发音
            </p>
          </motion.div>

          <Tabs value={activeTab} onValueChange={value => setActiveTab(value as TabValue)} className="w-full">
            <TabsList className="grid w-full grid-cols-3 h-14 md:h-16 mb-6 md:mb-8 rounded-2xl p-1.5 bg-card border border-border shadow-sm">
              <TabsTrigger
                value="pinyin"
                className="text-base md:text-lg font-semibold rounded-xl data-[state=active]:bg-lang-zh data-[state=active]:text-white transition-all"
              >
                🇨🇳 拼音
              </TabsTrigger>
              <TabsTrigger
                value="kana"
                className="text-base md:text-lg font-semibold rounded-xl data-[state=active]:bg-lang-jp data-[state=active]:text-white transition-all"
              >
                🇯🇵 五十音
              </TabsTrigger>
              <TabsTrigger
                value="alphabet"
                className="text-base md:text-lg font-semibold rounded-xl data-[state=active]:bg-lang-en data-[state=active]:text-white transition-all"
              >
                🇬🇧 ABC
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pinyin" className="space-y-6 md:space-y-8">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                <div className="bg-lang-zh-bg rounded-3xl p-5 md:p-8 shadow-md mb-6 md:mb-8 border-2 border-lang-zh-border">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 md:mb-6 text-lang-zh">声母 Initials</h2>
                  <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-3 md:gap-4">
                    {PINYIN_INITIALS.map(item => (
                      <motion.button
                        key={item.symbol}
                        onClick={() => handlePinyinClick(item.speakChar)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-white hover:bg-lang-zh-bg/50 rounded-2xl p-3 md:p-4 transition-all border-2 border-lang-zh-border hover:border-lang-zh shadow-sm hover:shadow-md"
                      >
                        <div className="text-3xl md:text-4xl mb-1">{item.emoji}</div>
                        <div className="text-2xl md:text-3xl font-bold text-lang-zh mb-1">
                          {item.symbol}
                        </div>
                        <div className="text-xs text-muted-foreground">{item.example}</div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="bg-lang-zh-bg rounded-3xl p-5 md:p-8 shadow-md border-2 border-lang-zh-border">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 md:mb-6 text-lang-zh">韵母 Finals</h2>
                  <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-3 md:gap-4">
                    {PINYIN_FINALS.map(item => (
                      <motion.button
                        key={item.symbol}
                        onClick={() => handlePinyinClick(item.speakChar)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-white hover:bg-lang-zh-bg/50 rounded-2xl p-3 md:p-4 transition-all border-2 border-lang-zh-border hover:border-lang-zh shadow-sm hover:shadow-md"
                      >
                        <div className="text-3xl md:text-4xl mb-1">{item.emoji}</div>
                        <div className="text-2xl md:text-3xl font-bold text-lang-zh mb-1">
                          {item.symbol}
                        </div>
                        <div className="text-xs text-muted-foreground">{item.example}</div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="kana" className="space-y-6">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                <div className="space-y-4 md:space-y-6">
                  {HIRAGANA_ROWS.map((hiRow, rowIdx) => {
                    const katRow = KATAKANA_ROWS[rowIdx];
                    return (
                      <div
                        key={hiRow.row}
                        className="bg-lang-jp-bg rounded-3xl p-5 md:p-6 shadow-md border-2 border-lang-jp-border"
                      >
                        <h3 className="text-xl md:text-2xl font-heading font-bold mb-3 md:mb-4 text-lang-jp">
                          {hiRow.row}
                        </h3>
                        <div className="grid grid-cols-5 gap-3 md:gap-4">
                          {hiRow.kanas.map((hiKana, cellIdx) => {
                            const katKana = katRow?.kanas[cellIdx];
                            return (
                              <motion.button
                                key={hiKana.char}
                                onClick={() => handleKanaClick(hiKana.char)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white hover:bg-lang-jp-bg/50 rounded-2xl p-3 md:p-4 transition-all border-2 border-lang-jp-border hover:border-lang-jp shadow-sm hover:shadow-md"
                              >
                                <div className="text-2xl md:text-3xl font-bold text-lang-jp leading-none">
                                  {hiKana.char}
                                </div>
                                {katKana && (
                                  <div className="text-lg md:text-xl font-bold text-lang-jp/60 leading-none mt-0.5">
                                    {katKana.char}
                                  </div>
                                )}
                                <div className="text-xs text-muted-foreground mt-1">{hiKana.romaji}</div>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="alphabet">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="bg-lang-en-bg rounded-3xl p-5 md:p-8 shadow-md border-2 border-lang-en-border"
              >
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 md:mb-6 text-lang-en">
                  26个字母 Alphabet
                </h2>
                <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 md:gap-4">
                  {ENGLISH_LETTERS.map(letter => (
                    <motion.button
                      key={letter.upper}
                      onClick={() => handleAlphabetClick(letter.upper)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-white hover:bg-lang-en-bg/50 rounded-2xl p-4 md:p-5 transition-all border-2 border-lang-en-border hover:border-lang-en shadow-sm hover:shadow-md"
                    >
                      <div className="text-3xl md:text-4xl mb-2">{letter.emoji}</div>
                      <div className="text-3xl md:text-4xl font-bold text-lang-en mb-1">
                        {letter.upper} {letter.lower}
                      </div>
                      <div className="text-xs text-muted-foreground">{letter.example}</div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}